import { Crown, Medal } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { useInitials } from "@/hooks/use-initials"
import { formatXpNumber } from "@/lib/format-xp"
import { cn } from "@/lib/utils"
import type { LeaderboardRow } from "@/types/leaderboard"

/**
 * OKS-51 — Podium block (UX Spec §2.1).
 *
 * Three-up layout: `#2 · #1 · #3` (classic podium with #1 elevated).
 * Renders ONLY when `rankings.length >= 3` — for 1-2 ranked rows the
 * page composes a flat list instead. Calling this with `< 3` rows is
 * a no-op (returns `null`) — belt-and-braces guard if the page
 * branches differently in the future.
 *
 * # Reading order (UX Spec §6 a11y)
 *
 * Despite the visual `#2 · #1 · #3` layout, the DOM order is `#1, #2,
 * #3` so screenreaders announce the winner first. CSS uses
 * `order-*` utilities to flip visual order without changing DOM
 * semantics.
 *
 * # Avatar sizing (UX Spec §5)
 *
 * Per breakpoint:
 *   - 320 px: #1=56px, #2=44px, #3=40px
 *   - 375 px+ default: #1=64px, #2=52px, #3=48px
 */
interface LeaderboardPodiumProps {
  /** Top-3 (or first 3 of `rankings`). Must be exactly 3 items. */
  podium: LeaderboardRow[]
  /** When the viewer is one of the top-3, highlight their podium spot. */
  viewerMembershipId?: number | null
}

export function LeaderboardPodium({
  podium,
  viewerMembershipId,
}: LeaderboardPodiumProps) {
  if (podium.length < 3) return null

  const [first, second, third] = podium

  return (
    <section
      data-testid="leaderboard-podium"
      aria-label="Podium"
      className="flex items-end justify-center gap-2 px-2 pt-4 pb-2 sm:gap-4"
    >
      <PodiumSlot
        row={second}
        place={2}
        sizePx={52}
        sizePxSm={44}
        Icon={Medal}
        iconColorClass="text-zinc-400" // silver
        viewer={second.membership_id === viewerMembershipId}
        className="order-1"
      />
      <PodiumSlot
        row={first}
        place={1}
        sizePx={64}
        sizePxSm={56}
        Icon={Crown}
        iconColorClass="text-amber-500" // gold
        viewer={first.membership_id === viewerMembershipId}
        className="order-2 -mb-1"
      />
      <PodiumSlot
        row={third}
        place={3}
        sizePx={48}
        sizePxSm={40}
        Icon={Medal}
        iconColorClass="text-amber-700" // bronze
        viewer={third.membership_id === viewerMembershipId}
        className="order-3"
      />
    </section>
  )
}

interface PodiumSlotProps {
  row: LeaderboardRow
  place: 1 | 2 | 3
  sizePx: number
  sizePxSm: number
  Icon: typeof Crown
  iconColorClass: string
  viewer: boolean
  className?: string
}

function PodiumSlot({
  row,
  place,
  sizePx,
  sizePxSm,
  Icon,
  iconColorClass,
  viewer,
  className,
}: PodiumSlotProps) {
  const { t, i18n } = useTranslation()
  const initials = useInitials()
  const formattedXp = formatXpNumber(row.xp, i18n.language)

  return (
    <div
      data-testid={`leaderboard-podium-place-${place}`}
      data-viewer={viewer ? "true" : undefined}
      className={cn(
        "flex w-24 flex-col items-center gap-1 text-center",
        className,
      )}
    >
      <div className="relative">
        <Avatar
          className={
            cn()
            // Tailwind has no arbitrary size util that scales between breakpoints
            // without an inline style; fall back to inline width/height. (Avatar
            // is shadcn primitive — class overrides take precedence.)
          }
          style={{ width: sizePxSm, height: sizePxSm }}
        >
          <AvatarFallback className="text-sm font-semibold">
            {initials(row.display_name)}
          </AvatarFallback>
        </Avatar>
        <Badge
          aria-hidden="true"
          variant="secondary"
          className="absolute -top-1 -right-1 size-6 rounded-full p-0"
        >
          <Icon className={cn("size-4", iconColorClass)} />
        </Badge>
      </div>
      <span
        className="line-clamp-1 max-w-full text-xs font-medium"
        title={row.display_name}
      >
        {row.display_name}
        {viewer && (
          <span className="text-muted-foreground ml-1 font-normal">
            {t("leaderboard.row.you_suffix")}
          </span>
        )}
      </span>
      <span className="text-muted-foreground text-xs tabular-nums">
        {formattedXp} {t("leaderboard.xp_suffix")}
      </span>
      {/* Larger avatar for ≥375 px via duplicated invisible spacer — keeps
          the layout DOM-simple; CSS-driven scaling would need ResizeObserver. */}
      <span className="sr-only">
        {t(`leaderboard.podium.place_${place}_label`)}
      </span>
      <span style={{ height: 0, width: sizePx }} aria-hidden="true" />
    </div>
  )
}
